/**
 * Attribute-agnostic single-select toggle for a `PropertyFilter` chip
 * inside a `PropertyFilterGroup`. Pure and non-mutating.
 *
 * The FilterBar rehydrate step expects the root of the group to hold at
 * most two children: an optional top-level `asin` `PropertyFilter` and
 * one nested `PropertyFilterGroup` for every other attribute filter.
 * The target chip lives inside the nested group; nothing is written at
 * the root.
 *
 * Caller-provided `isMemberOfSelectGroup` predicate decides which
 * existing chips constitute the "select group" — the set of chips that
 * should be replaced by the target. That lets simple single-select
 * attributes strip by `propertyKey` while attributes that pack multiple
 * logical dimensions into one key (like `validationStatus`, which
 * prefixes the value with a bar identifier) can strip by value prefix
 * and preserve chips belonging to sibling dimensions.
 *
 * Toggle-off behavior: if the exact target chip
 * (`(newFilter.propertyKey, newFilter.value)`) already exists in the
 * nested group, all select-group members are stripped and the target is
 * not re-appended.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "./searchSideBarConnector";

/**
 * Returns a fresh `PropertyFilterGroup` with the target token added,
 * replaced, or toggled off per the rules above. `current` is never
 * mutated. Non-matching tokens in the nested group and the root-level
 * `asin` filter are preserved untouched.
 */
/** The attribute filters of a query root, decomposed for editing. */
export interface AttributeFilterRoot {
  /** The single top-level `asin` PropertyFilter, if present. */
  readonly asinFilter: PropertyFilter | undefined;
  /**
   * The flat list of attribute filters to strip/append within — the
   * direct children of the canonical nested attribute group, OR, when
   * the root is denormalized, every non-`asin` root filter itself.
   */
  readonly attributeFilters: ReadonlyArray<
    PropertyFilter | PropertyFilterGroup
  >;
  /** Operation to give the rebuilt attribute group. */
  readonly groupOperation: "and" | "or";
}

/**
 * Decompose a query root into its optional top-level `asin` filter and
 * the flat list of attribute filters to operate on.
 *
 * This is the SINGLE place that knows how to locate "the attribute
 * filters", so the toggle / strip / drill-down helpers can never again
 * disagree about it and silently drop root-level siblings.
 *
 * It tolerates two shapes:
 *  - Canonical: root = `[asin?, one nested attribute PropertyFilterGroup]`.
 *    The attribute filters are that group's direct children, and its
 *    operation is preserved (unchanged from the previous behavior).
 *  - Denormalized: the root holds multiple attribute filters directly
 *    (several groups and/or scalar chips — e.g. a predefined date
 *    subgroup + a Product Line OR-group + a Program scalar). Every
 *    non-`asin` root filter is kept as an attribute filter, so none is
 *    lost when the query is rebuilt.
 */
export function splitAttributeFilters(
  current: PropertyFilterGroup
): AttributeFilterRoot {
  const asinFilter = current.filters.find(
    (f): f is PropertyFilter =>
      f.type === "PropertyFilter" && f.propertyKey === "asin"
  );
  const nonAsin = current.filters.filter((f) => f !== asinFilter);

  const isCanonical =
    nonAsin.length === 1 && nonAsin[0].type === "PropertyFilterGroup";
  if (isCanonical) {
    const grp = nonAsin[0] as PropertyFilterGroup;
    return {
      asinFilter,
      attributeFilters: grp.filters ?? [],
      groupOperation: grp.operation ?? "and",
    };
  }
  return { asinFilter, attributeFilters: nonAsin, groupOperation: "and" };
}

/**
 * Re-assemble the canonical root shape — `[asin?, one attribute group]`
 * — from an edited attribute-filter list. An empty attribute list
 * drops the group entirely (so the last removed chip clears the query).
 * Keeping the output canonical guarantees it round-trips through
 * `sumPropertyFilterQueries -> toString -> spreadPropertyFilterQueries`
 * (which caps the root at two sub-queries).
 */
export function rebuildAttributeRoot(
  current: PropertyFilterGroup,
  asinFilter: PropertyFilter | undefined,
  groupOperation: "and" | "or",
  nextAttributeFilters: Array<PropertyFilter | PropertyFilterGroup>
): PropertyFilterGroup {
  const rootFilters: Array<PropertyFilter | PropertyFilterGroup> = [];
  if (asinFilter !== undefined) {
    rootFilters.push(asinFilter);
  }
  if (nextAttributeFilters.length > 0) {
    rootFilters.push({
      type: "PropertyFilterGroup",
      operation: groupOperation,
      filters: nextAttributeFilters,
    });
  }
  return {
    type: "PropertyFilterGroup",
    operation: current.operation,
    filters: rootFilters,
  };
}

export function toggleSingleSelectFilterToken(
  current: PropertyFilterGroup,
  newFilter: PropertyFilter,
  isMemberOfSelectGroup: (filter: PropertyFilter) => boolean
): PropertyFilterGroup {
  const { asinFilter, attributeFilters, groupOperation } =
    splitAttributeFilters(current);

  const isExactTarget = (
    f: PropertyFilter | PropertyFilterGroup
  ): f is PropertyFilter =>
    f.type === "PropertyFilter" &&
    f.propertyKey === newFilter.propertyKey &&
    f.value === newFilter.value;

  const hasTarget = attributeFilters.some(isExactTarget);

  const strippedGroupFilters = attributeFilters.filter(
    (f) => f.type !== "PropertyFilter" || !isMemberOfSelectGroup(f)
  );

  const nextGroupFilters: Array<PropertyFilter | PropertyFilterGroup> =
    hasTarget
      ? [...strippedGroupFilters]
      : [...strippedGroupFilters, newFilter];

  return rebuildAttributeRoot(
    current,
    asinFilter,
    groupOperation,
    nextGroupFilters
  );
}
